import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  BellIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  KeyIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../services/api';

const CompanySettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const { user } = useAuth();
  const { companyData, refreshCompanyData } = useCompany();

  const [settings, setSettings] = useState({
    general: {
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      companyAddress: '',
      timeZone: 'UTC',
      dateFormat: 'DD/MM/YYYY',
      currency: 'USD'
    },
    features: {
      enableLeadScoring: true,
      enableAutoFollowup: false,
      enableEmailIntegration: true,
      enableSMSNotifications: false,
      enableFileAttachments: true,
      enableAdvancedReporting: true
    },
    notifications: {
      emailNewLead: true,
      emailLeadConverted: true,
      emailFollowupDue: true,
      emailWeeklyReport: false,
      smsFollowupReminder: false,
      smsLeadAssignment: false
    },
    security: {
      requirePasswordChange: false,
      passwordExpiryDays: 90,
      enableTwoFactor: false,
      sessionTimeoutMinutes: 60,
      allowedIPAddresses: ''
    },
    customization: {
      primaryColor: '#3B82F6',
      secondaryColor: '#8B5CF6',
      logoUrl: '',
      companyTheme: 'default'
    }
  });

  useEffect(() => {
    fetchCompanySettings();
  }, [companyData]);

  // Update company name and email when companyData changes
  useEffect(() => {
    if (companyData && (companyData.name || companyData.email)) {
      setSettings(prev => ({
        ...prev,
        general: {
          ...prev.general,
          companyName: typeof companyData.name === 'string' ? companyData.name : prev.general.companyName,
          companyEmail: typeof companyData.email === 'string' ? companyData.email : prev.general.companyEmail
        }
      }));
    }
  }, [companyData]);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies/settings');
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          general: {
            ...prev.general,
            ...response.data.general,
            companyName: (typeof companyData?.name === 'string' ? companyData.name : null) ||
                        (typeof response.data.general?.companyName === 'string' ? response.data.general.companyName : null) ||
                        prev.general.companyName,
            companyEmail: (typeof companyData?.email === 'string' ? companyData.email : null) ||
                         (typeof response.data.general?.companyEmail === 'string' ? response.data.general.companyEmail : null) ||
                         prev.general.companyEmail,
            companyPhone: typeof response.data.general?.companyPhone === 'string' ? response.data.general.companyPhone : prev.general.companyPhone,
            companyAddress: typeof response.data.general?.companyAddress === 'string' ? response.data.general.companyAddress : prev.general.companyAddress,
            timeZone: typeof response.data.general?.timeZone === 'string' ? response.data.general.timeZone : prev.general.timeZone
          }
        }));
      } else if (companyData) {
        // If no settings exist, use company data
        setSettings(prev => ({
          ...prev,
          general: {
            ...prev.general,
            companyName: typeof companyData.name === 'string' ? companyData.name : prev.general.companyName,
            companyEmail: typeof companyData.email === 'string' ? companyData.email : prev.general.companyEmail,
            companyPhone: typeof companyData.phone === 'string' ? companyData.phone : prev.general.companyPhone,
            companyAddress: typeof companyData.address === 'string' ? companyData.address : prev.general.companyAddress
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use company data from context as fallback
      if (companyData) {
        setSettings(prev => ({
          ...prev,
          general: {
            ...prev.general,
            companyName: typeof companyData.name === 'string' ? companyData.name : prev.general.companyName,
            companyEmail: typeof companyData.email === 'string' ? companyData.email : prev.general.companyEmail,
            companyPhone: typeof companyData.phone === 'string' ? companyData.phone : prev.general.companyPhone,
            companyAddress: typeof companyData.address === 'string' ? companyData.address : prev.general.companyAddress
          }
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (section) => {
    try {
      setSaving(true);
      await api.put('/companies/settings', {
        section,
        settings: settings[section]
      });
      toast.success('Settings saved successfully');
      
      // Refresh company data if general or customization settings changed
      if (section === 'general' || section === 'customization') {
        await refreshCompanyData();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const tabs = [
    { id: 'general', name: 'General', icon: BuildingOfficeIcon },
    // { id: 'features', name: 'Features', icon: Cog6ToothIcon },
    // { id: 'notifications', name: 'Notifications', icon: BellIcon },
    // { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    // { id: 'customization', name: 'Customization', icon: PaintBrushIcon }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading settings..." />
        </div>
      </Layout>
    );
  }

  const renderGeneralSettings = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Company Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-900">Company Name</h3>
              <p className="text-xs text-blue-600">Registered company name</p>
            </div>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {typeof settings.general.companyName === 'string' ? settings.general.companyName : 'Loading...'}
          </p>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-green-900">Company Email</h3>
              <p className="text-xs text-green-600">Primary contact email</p>
            </div>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {typeof settings.general.companyEmail === 'string' ? settings.general.companyEmail : 'Loading...'}
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-purple-900">Phone Number</h3>
              <p className="text-xs text-purple-600">Contact phone number</p>
            </div>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {typeof settings.general.companyPhone === 'string' ? settings.general.companyPhone : 'Not set'}
          </p>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Cog6ToothIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-orange-900">Time Zone</h3>
              <p className="text-xs text-orange-600">Company time zone</p>
            </div>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {typeof settings.general.timeZone === 'string' ? settings.general.timeZone : 'UTC'}
          </p>
        </div>
      </div>

      {/* Company Address Card */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <KeyIcon className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-teal-900">Company Address</h3>
            <p className="text-xs text-teal-600">Registered business address</p>
          </div>
        </div>
        <p className="text-lg font-bold text-gray-900 whitespace-pre-line">
          {typeof settings.general.companyAddress === 'string' ? settings.general.companyAddress : 'Not set'}
        </p>
      </div>
    </motion.div>
  );

  const renderFeaturesSettings = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(settings.features).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div>
              <label className="text-sm font-medium text-gray-700 cursor-pointer">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {getFeatureDescription(key)}
              </p>
            </div>
            <button
              onClick={() => updateSetting('features', key, !value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                value ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  value ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );

  const renderNotificationsSettings = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">Email Notifications</h4>
        {Object.entries(settings.notifications)
          .filter(([key]) => key.startsWith('email'))
          .map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-700 cursor-pointer">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^email/, '').replace(/^./, str => str.toUpperCase())}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {getNotificationDescription(key)}
                </p>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => updateSetting('notifications', key, e.target.checked)}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          ))}
      </div>
      
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">SMS Notifications</h4>
        {Object.entries(settings.notifications)
          .filter(([key]) => key.startsWith('sms'))
          .map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-700 cursor-pointer">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^sms/, '').replace(/^./, str => str.toUpperCase())}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {getNotificationDescription(key)}
                </p>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => updateSetting('notifications', key, e.target.checked)}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          ))}
      </div>
    </motion.div>
  );

  const renderSecuritySettings = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Require Password Change
            </label>
            <p className="text-xs text-gray-500 mt-1">Force users to change password periodically</p>
          </div>
          <input
            type="checkbox"
            checked={settings.security.requirePasswordChange}
            onChange={(e) => updateSetting('security', 'requirePasswordChange', e.target.checked)}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Password Expiry (Days)
          </label>
          <input
            type="number"
            value={settings.security.passwordExpiryDays}
            onChange={(e) => updateSetting('security', 'passwordExpiryDays', parseInt(e.target.value))}
            min="30"
            max="365"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
          />
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Enable Two-Factor Authentication
            </label>
            <p className="text-xs text-gray-500 mt-1">Require 2FA for all users</p>
          </div>
          <input
            type="checkbox"
            checked={settings.security.enableTwoFactor}
            onChange={(e) => updateSetting('security', 'enableTwoFactor', e.target.checked)}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Session Timeout (Minutes)
          </label>
          <input
            type="number"
            value={settings.security.sessionTimeoutMinutes}
            onChange={(e) => updateSetting('security', 'sessionTimeoutMinutes', parseInt(e.target.value))}
            min="15"
            max="480"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Allowed IP Addresses (Optional)
        </label>
        <textarea
          value={settings.security.allowedIPAddresses}
          onChange={(e) => updateSetting('security', 'allowedIPAddresses', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
          placeholder="Enter IP addresses separated by commas (e.g., 192.168.1.1, 10.0.0.0/24)"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty to allow access from any IP address</p>
      </div>
    </motion.div>
  );

  const renderCustomizationSettings = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Primary Color
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.customization.primaryColor}
              onChange={(e) => updateSetting('customization', 'primaryColor', e.target.value)}
              className="h-12 w-20 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={settings.customization.primaryColor}
              onChange={(e) => updateSetting('customization', 'primaryColor', e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
              placeholder="#3B82F6"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Secondary Color
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.customization.secondaryColor}
              onChange={(e) => updateSetting('customization', 'secondaryColor', e.target.value)}
              className="h-12 w-20 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={settings.customization.secondaryColor}
              onChange={(e) => updateSetting('customization', 'secondaryColor', e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
              placeholder="#8B5CF6"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Company Logo URL
          </label>
          <input
            type="url"
            value={settings.customization.logoUrl}
            onChange={(e) => updateSetting('customization', 'logoUrl', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
            placeholder="https://example.com/logo.png"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Theme
          </label>
          <select
            value={settings.customization.companyTheme}
            onChange={(e) => updateSetting('customization', 'companyTheme', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
          >
            <option value="default">Default</option>
            <option value="dark">Dark</option>
            <option value="minimal">Minimal</option>
            <option value="corporate">Corporate</option>
          </select>
        </div>
      </div>
      
      {settings.customization.logoUrl && (
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">Logo Preview</p>
          <img
            src={settings.customization.logoUrl}
            alt="Company Logo"
            className="h-16 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </motion.div>
  );

  const getFeatureDescription = (key) => {
    const descriptions = {
      enableLeadScoring: 'Automatically score leads based on engagement',
      enableAutoFollowup: 'Send automated follow-up reminders',
      enableEmailIntegration: 'Integrate with email providers',
      enableSMSNotifications: 'Send SMS notifications to users',
      enableFileAttachments: 'Allow file uploads for leads and customers',
      enableAdvancedReporting: 'Access advanced analytics and reports'
    };
    return descriptions[key] || 'Configure this feature setting';
  };

  const getNotificationDescription = (key) => {
    const descriptions = {
      emailNewLead: 'Get notified when new leads are created',
      emailLeadConverted: 'Get notified when leads become customers',
      emailFollowupDue: 'Get reminders for upcoming follow-ups',
      emailWeeklyReport: 'Receive weekly performance reports',
      smsFollowupReminder: 'SMS reminders for follow-ups',
      smsLeadAssignment: 'SMS notifications for lead assignments'
    };
    return descriptions[key] || 'Configure this notification setting';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 p-8 mb-8 shadow-2xl"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

            <div className="relative z-10 flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Cog6ToothIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Company Settings</h1>
                <p className="text-indigo-100 text-lg">
                  Configure your company preferences and policies
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            {/* <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div> */}

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-8">
                  {/* <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {tabs.find(tab => tab.id === activeTab)?.name} Settings
                    </h2>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSaveSettings(activeTab)}
                      disabled={saving}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <CheckIcon className="h-4 w-4" />
                          <span>Save Changes</span>
                        </div>
                      )}
                    </motion.button>
                  </div> */}

                  {activeTab === 'general' && renderGeneralSettings()}
                  {activeTab === 'features' && renderFeaturesSettings()}
                  {activeTab === 'notifications' && renderNotificationsSettings()}
                  {activeTab === 'security' && renderSecuritySettings()}
                  {activeTab === 'customization' && renderCustomizationSettings()}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CompanySettings;